import { components } from "@/api-schema";
import { useModelGenerationSearchQuery } from "@/common/api/model-generation/use-model-generation-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

export type QueryBody = components["schemas"]["ModelGenerationSearchBody"];

const FILTER_PAGE_SIZE = 50;

// Custom hook to fetch model generation data with pagination
function useModelGenerationFilterData(body: QueryBody): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: modelGenerations,
    isPending: isLoading,
    error,
  } = useModelGenerationSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
      },
      body,
    },
  });

  const options =
    modelGenerations?.data
      .map((element) => ({
        id: element.uuid ?? "",
        name: element.name ?? "",
      }))
      .filter((option) => option.id && option.name)
      .filter((option, index, array) => array.findIndex((item) => item.id === option.id) === index) || [];

  const loadedCount = modelGenerations?.data?.length ?? 0;
  const total = modelGenerations?.meta?.total ?? 0;

  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: {
      options,
      isLoading,
      error,
    },
    pagination,
  };
}

export class ModelGenerationFilter extends Filter<QueryBody, unknown> {
  readonly id: string = "modelGenerations";
  readonly labelSingular = "common:Model";
  readonly labelPlural = "common:Models";
  readonly placeholder = "common:SelectModels";
  readonly searchPlaceholder = "common:SearchModels";
  readonly emptyText = "common:NoModelsFound";

  constructor({ id, body = {} }: { id?: string; body?: Partial<QueryBody> } = {}) {
    super();
    if (id) this.id = id;
    this.body = body as QueryBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <ModelGenerationFilterComponent filter={this} {...props} />;
  }
}

function ModelGenerationFilterComponent({ filter, ...props }: { filter: ModelGenerationFilter } & BaseFilterProps) {
  const { data, pagination } = useModelGenerationFilterData(filter.body);

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
